/**
 * The affiliate limits a reseller's commission plans are checked against (TMMPDM-0089, tmm-api 0154):
 * the live controls in force on the rate card the reseller adopted. Before 0154 they were the
 * agreement card's; the agreement no longer carries a card. The adopted card is the one whose
 * `adoptingResellers` holds the reseller (ids the server sent). The limits are shown as hints only;
 * the plan's verdict stays the server's.
 */
import { useEffect, useState } from 'react';
import { adminApi } from '@/api/tmm/admin';

export interface AdoptedCardLimits {
  cardName: string;
  currency: string;
  recurringMaxBp: number;
  coolOffMaxDays: number;
  bountyMaxMinor: number;
  milestoneMaxMinor: number;
}

export interface UseAdoptedCardLimits {
  /** `null` while loading, after a failure, or when the reseller has adopted no card. */
  limits: AdoptedCardLimits | null;
  status: 'loading' | 'ready' | 'none' | 'failed';
  error: unknown;
  retry: () => void;
}

export function useAdoptedCardLimits(resellerId: number): UseAdoptedCardLimits {
  const [attempt, setAttempt] = useState(0);
  const [state, setState] = useState<Omit<UseAdoptedCardLimits, 'retry'>>({
    limits: null,
    status: 'loading',
    error: null,
  });

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      const list = await adminApi.listCustomerRateCards();
      const adopted = list.rateCards.find((card) => card.adoptingResellers.includes(resellerId));
      if (!adopted) return null;
      const controls = await adminApi.getCardControls(adopted.id);
      return {
        cardName: adopted.name,
        currency: controls.currency,
        recurringMaxBp: controls.current.affiliateRecurringMaxBp,
        coolOffMaxDays: controls.current.affiliateCoolOffMaxDays,
        bountyMaxMinor: controls.current.affiliateBountyMaxMinor,
        milestoneMaxMinor: controls.current.affiliateMilestoneMaxMinor,
      };
    };
    load()
      .then((limits) => {
        if (cancelled) return;
        setState({ limits, status: limits ? 'ready' : 'none', error: null });
      })
      .catch((error: unknown) => {
        if (cancelled) return;
        setState({ limits: null, status: 'failed', error });
      });
    return () => {
      cancelled = true;
    };
  }, [resellerId, attempt]);

  const retry = () => {
    setState({ limits: null, status: 'loading', error: null });
    setAttempt((n) => n + 1);
  };

  return { ...state, retry };
}
